import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import {
  Home,
  User,
  Wallet,
  Layout,
  HelpCircle,
  Settings,
  LogOut,
  Menu,
  X,
  ChevronRight,
  ArrowLeft,
  Shield,
  CreditCard,
  FileText,
  Globe,
  Eye,
  Network,
  Database,
  ExternalLink,
  ShoppingBag,
  Zap,
  Users,
  BarChart3,
  BookOpen,
  MessageSquare,
  PlayCircle,
  Template,
  Users2,
  QrCode,
  Crown,
  TrendingUp,
  Gamepad2,
  DollarSign,
  Scissors,
  Wrench,
  Bell,
  Coins,
  Package
} from 'lucide-react';

const Navigation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const isActive = (path) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const memberNavItems = [
    // Core Dashboard
    { path: '/dashboard', label: 'Dashboard', icon: Home },
    
    // Education Section (Prominent Placement)
    { 
      label: 'EDUCATION', 
      type: 'section',
      items: [
        { path: '/member/boot-camp-guide', label: 'Boot Camp Guide', icon: BookOpen },
        { path: '/member/training-vault', label: 'Boot Camp eBooks', icon: BookOpen },
        { path: '/member/training-courses', label: 'Training Courses', icon: PlayCircle },
        { path: '/member/marketing-library', label: 'Marketing Library', icon: FileText },
      ]
    },
    
    // Tools Section  
    { 
      label: 'TOOLS',
      type: 'section', 
      items: [
        { path: '/member/rookie-toolkit', label: 'Rookie Toolkit', icon: Scissors },
        { path: '/member/lead-capture/marketplace', label: 'Lead Capture', icon: Zap },
        { path: '/pages', label: 'Page Builder', icon: Layout },
        { path: '/member/safe-list', label: 'Safe List Messages', icon: MessageSquare },
        { path: '/qr-generator', label: 'QR Generator', icon: QrCode },
        { path: '/rookie-cheat-sheet', label: 'Rookie Cheat Sheet', icon: FileText },
      ]
    },
    
    // Growth Section
    {
      label: 'GROWTH',
      type: 'section',
      items: [
        { path: '/member/game', label: 'Game Dashboard', icon: Gamepad2 },
        { path: '/member/tools/store', label: 'Tool Store', icon: Wrench },
        { path: '/member/tools/inventory', label: 'My Tools', icon: Package },
        { path: '/member/ad-credits', label: 'Ad Credits', icon: DollarSign },
        { path: '/member/gamification', label: 'Progress & XP', icon: TrendingUp },
        { path: '/member/founders', label: 'Founder Dashboard', icon: Crown },
        { path: '/member/lead-capture/leads', label: 'My Leads', icon: Users },
        { path: '/mlm', label: 'Matrix', icon: Network },
        { path: '/network', label: 'Network Feed', icon: Globe },
      ]
    },
    
    // Account Section
    {
      label: 'ACCOUNT', 
      type: 'section',
      items: [
        { path: '/wallet', label: 'My Wallet', icon: Wallet },
        { path: '/member/credits-wallet', label: 'Credits Wallet', icon: Coins },
        { path: '/member/notifications', label: 'Notifications', icon: Bell },
        { path: '/credits', label: 'Credits & Access', icon: CreditCard },
        { path: '/marketplace', label: 'Marketplace', icon: ShoppingBag }
      ]
    },
    
    // Support Section
    {
      label: 'SUPPORT',
      type: 'section',
      items: [
        { path: '/faq', label: 'FAQ', icon: HelpCircle },
        { path: '/terms', label: 'Terms & Conditions', icon: FileText },
        { path: '/privacy-policy', label: 'Privacy Policy', icon: Shield }
      ]
    }
  ];

  const adminNavItems = [
    { path: '/admin/dashboard', label: 'Admin Dashboard', icon: Shield },
    { path: '/admin/users', label: 'Users', icon: User },
    { path: '/admin/founders', label: 'Founders Panel', icon: Crown },
    { path: '/admin/waitlist', label: 'Waitlist Launch', icon: Users2 },
    { path: '/admin/templates', label: 'Template Management', icon: Layout },
    { path: '/admin/holding-tank', label: 'Holding Tank', icon: Users },
    { path: '/admin/safe-list', label: 'Safe List Admin', icon: MessageSquare },
    { path: '/admin/mlm', label: 'MLM System', icon: Network },
    { path: '/admin/marketing', label: 'Marketing Manager', icon: BookOpen },
    { path: '/admin/training-vault', label: 'Training Vault', icon: BarChart3 },
    { path: '/admin/configuration', label: 'Configuration Guide', icon: Settings },
    { path: '/admin/backup', label: 'Database Backup', icon: Database },
    { path: '/admin/external-api', label: 'External API', icon: ExternalLink },
    { path: '/admin/nowpayments', label: 'NOWPayments', icon: CreditCard },
    { path: '/admin/faq', label: 'FAQ Management', icon: FileText },
    { path: '/admin/network', label: 'Network Feed', icon: Globe }
  ];

  // Don't show navigation on public pages
  if (!user || location.pathname === '/' || location.pathname === '/join' || location.pathname.startsWith('/holding/') || location.pathname === '/login' || location.pathname === '/register' || location.pathname === '/member/login' || location.pathname === '/member/register') {
    return null;
  }

  const navItems = user.role === 'admin' ? adminNavItems : memberNavItems;

  return (
    <>
      {/* Mobile Menu Button */}
      <div className="md:hidden fixed top-4 left-4 z-50">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="bg-white shadow-md"
        >
          {isMobileMenuOpen ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
        </Button>
      </div>

      {/* Back Button for Mobile */}
      <div className="md:hidden fixed top-4 right-4 z-50">
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate(-1)}
          className="bg-white shadow-md"
        >
          <ArrowLeft className="w-4 h-4" />
        </Button>
      </div>

      {/* Desktop Sidebar */}
      <div className="hidden md:flex md:flex-col md:fixed md:inset-y-0 md:w-64 md:bg-gray-900">
        <div className="flex flex-col flex-1 min-h-0 h-screen">
          {/* Logo */}
          <div className="flex items-center h-16 flex-shrink-0 px-4 bg-gray-900">
            <div className="flex items-center">
              <div className="bg-yellow-500 text-black px-2 py-1 rounded font-bold text-sm mr-3">
                BBC
              </div>
              <span className="text-white font-medium">Quick List</span>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 min-h-0 px-2 py-4 space-y-1 overflow-y-auto">
            {navItems.map((item, index) => {
              // Handle sectioned navigation (member)
              if (item.type === 'section') {
                return (
                  <div key={item.label} className="space-y-3">
                    {/* Section Header */}
                    <div className="px-2 py-2">
                      <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                        {item.label}
                      </h3>
                    </div>
                    {/* Section Items */}
                    <div className="space-y-1">
                      {item.items.map((subItem) => {
                        const Icon = subItem.icon;
                        return (
                          <button
                            key={subItem.path}
                            onClick={() => navigate(subItem.path)}
                            className={`w-full group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors ${
                              isActive(subItem.path)
                                ? 'bg-gray-800 text-white'
                                : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                            }`}
                          >
                            <Icon className="mr-3 h-5 w-5 flex-shrink-0" />
                            {subItem.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              }
              
              // Handle flat navigation (admin or legacy member items)
              const Icon = item.icon;
              return (
                <button
                  key={item.path}
                  onClick={() => navigate(item.path)}
                  className={`w-full group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors ${
                    isActive(item.path)
                      ? 'bg-gray-800 text-white'
                      : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                  }`}
                >
                  <Icon className="mr-3 h-5 w-5 flex-shrink-0" />
                  {item.label}
                </button>
              );
            })}
          </nav>

          {/* User Profile */}
          <div className="flex-shrink-0 p-4 border-t border-gray-700">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-yellow-500 rounded-full flex items-center justify-center">
                  <User className="w-4 h-4 text-gray-900" />
                </div>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-white">{user.full_name || user.username}</p>
                <p className="text-xs text-gray-400 capitalize">{user.role}</p>
              </div>
            </div>
            <div className="mt-3 space-y-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                className="w-full justify-start text-gray-300 hover:text-white hover:bg-gray-700"
              >
                <LogOut className="mr-2 h-4 w-4" />
                Sign Out
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Sidebar */}
      {isMobileMenuOpen && (
        <div className="md:hidden fixed inset-0 z-40 flex">
          <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setIsMobileMenuOpen(false)} />
          <div className="relative flex flex-col max-w-xs w-full h-full bg-gray-900">
            <div className="flex items-center h-16 flex-shrink-0 px-4">
              <div className="flex items-center">
                <div className="bg-yellow-500 text-black px-2 py-1 rounded font-bold text-sm mr-3">
                  BBC
                </div>
                <span className="text-white font-medium">Quick List</span>
              </div>
            </div>

            <nav className="flex-1 min-h-0 px-2 py-4 space-y-1 overflow-y-auto">
              {navItems.map((item, index) => {
                // Handle sectioned navigation (member)
                if (item.type === 'section') {
                  return (
                    <div key={item.label} className="space-y-3">
                      {/* Section Header */}
                      <div className="px-2 py-2">
                        <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                          {item.label}
                        </h3>
                      </div>
                      {/* Section Items */}
                      <div className="space-y-1">
                        {item.items.map((subItem) => {
                          const Icon = subItem.icon;
                          return (
                            <button
                              key={subItem.path}
                              onClick={() => {
                                navigate(subItem.path);
                                setIsMobileMenuOpen(false);
                              }}
                              className={`w-full group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors ${
                                isActive(subItem.path)
                                  ? 'bg-gray-800 text-white'
                                  : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                              }`}
                            >
                              <Icon className="mr-3 h-5 w-5 flex-shrink-0" />
                              {subItem.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                }
                
                // Handle flat navigation (admin or legacy member items)  
                const Icon = item.icon;
                return (
                  <button
                    key={item.path}
                    onClick={() => {
                      navigate(item.path);
                      setIsMobileMenuOpen(false);
                    }}
                    className={`w-full group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
                      isActive(item.path)
                        ? 'bg-gray-800 text-white'
                        : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                    }`}
                  >
                    <Icon className="mr-3 h-5 w-5 flex-shrink-0" />
                    {item.label}
                  </button>
                );
              })}
            </nav>

            <div className="flex-shrink-0 p-4 border-t border-gray-700">
              <div className="flex items-center mb-3">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-yellow-500 rounded-full flex items-center justify-center">
                    <User className="w-4 h-4 text-gray-900" />
                  </div>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-white">{user.full_name || user.username}</p>
                  <p className="text-xs text-gray-400 capitalize">{user.role}</p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                className="w-full justify-start text-gray-300 hover:text-white hover:bg-gray-700"
              >
                <LogOut className="mr-2 h-4 w-4" />
                Sign Out
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Navigation;